import { Component, ChangeDetectionStrategy, inject, computed, input } from "@angular/core";
import { RouterModule } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";
import { TagModule } from "primeng/tag";
import { RippleModule } from "primeng/ripple";

import { QuoteStatus } from "src/api-service";
import { getQuoteStatusIcon, getQuoteStatusSeverity } from "src/app/shared/util/quote-status.utils";
import { selectIsSubscriptionQuote, selectQuoteId } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion, selectIsVersionLocked, selectVersionLockMessage } from "src/app/store/pages/quote/version/version.selectors";
import { UserContext } from "src/app/store/user/user.state";
import { NavRoutesService } from "src/app/navigation/nav-routes.service";
import { NavContextHeaderComponent } from "../../parts/nav-context-header/nav-context-header.component";

@Component({
    selector: "app-quote-view",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, TranslateModule, TooltipModule, TagModule, RippleModule, NavContextHeaderComponent],
    templateUrl: "./quote-view.component.html",
    styleUrl: "./quote-view.component.scss",
})
export class QuoteViewComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly navRoutes = inject(NavRoutesService);

    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly expanded = input.required<boolean>();
    public readonly user = input.required<UserContext | null>();

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly isVersionLocked = toSignal(this.store.select(selectIsVersionLocked), { initialValue: false });
    private readonly versionLockMessage = toSignal(this.store.select(selectVersionLockMessage));
    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });
    private readonly quoteId = toSignal(this.store.select(selectQuoteId), { initialValue: null });

    // =========================================================================
    // COMPUTED - Lock tooltip (version section)
    // =========================================================================
    public readonly versionLockTooltip = computed(() => {
        const lockMsg = this.versionLockMessage();
        return this.isVersionLocked() && lockMsg?.message ? lockMsg.message : null;
    });

    // =========================================================================
    // COMPUTED - Articles label (always includes catalogs)
    // =========================================================================
    public readonly articlesLabel = "QUOTE.DETAIL.TABS.ITEMS_AND_CATALOGS";

    // =========================================================================
    // COMPUTED - Status
    // =========================================================================
    public readonly versionStatusIcon = computed(() => getQuoteStatusIcon(this.selectedVersion()?.status).icon);
    public readonly versionStatusSeverity = computed(() => getQuoteStatusSeverity(this.selectedVersion()?.status));
    public readonly versionStatusLabel = computed(() => `VERSION.STATUS.${QuoteStatus[this.selectedVersion()?.status ?? 0]?.toUpperCase() ?? "DRAFT"}`);

    // =========================================================================
    // COMPUTED - Routes
    // =========================================================================
    public readonly baseRoute = computed<string[] | null>(() => {
        const qId = this.quoteId();
        return qId ? this.navRoutes.route("quote", qId) : null;
    });

    public readonly quotesListRoute = computed(() => this.navRoutes.route("quote"));

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public tabRoute(tab: string): string[] | null {
        const base = this.baseRoute();
        return base ? [...base, tab] : null;
    }

    public nestedTabRoute(...tabs: string[]): string[] | null {
        const base = this.baseRoute();
        return base ? [...base, ...tabs] : null;
    }
}
